import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import AdminLayout from "@/components/admin/AdminLayout";
import axios from "axios";
import { Search, Eye, MessageSquare, FileText, Filter } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from "@/components/ui/select";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  documents_required: "bg-amber-500/20 text-amber-400",
  under_review: "bg-purple-500/20 text-purple-400",
  proposal_drafting: "bg-indigo-500/20 text-indigo-400",
  creditor_voting: "bg-cyan-500/20 text-cyan-400",
  active: "bg-emerald-500/20 text-emerald-400",
  completed: "bg-green-600/20 text-green-400"
};

const AdminCasesPage = () => {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    fetchCases();
  }, []);

  const fetchCases = async () => {
    const token = localStorage.getItem("admin_token");
    try {
      const response = await axios.get(`${API}/admin/cases`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCases(response.data);
    } catch (error) {
      console.error("Cases error:", error);
      toast.error("Failed to load cases");
    } finally {
      setLoading(false);
    }
  };

  const updateCaseStatus = async (caseId, status) => {
    const token = localStorage.getItem("admin_token");
    try {
      await axios.put(`${API}/admin/cases/${caseId}/status`, 
        { status },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success("Case status updated");
      fetchCases();
    } catch (error) {
      console.error("Update error:", error);
      toast.error("Failed to update status");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const filteredCases = cases.filter(c => {
    const userName = c.user?.full_name || '';
    const userEmail = c.user?.email || '';
    const matchesSearch = userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         userEmail.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || c.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#10B981] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-cases">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white mb-2">Cases</h1>
            <p className="text-slate-400">Manage client IVA cases</p>
          </div>
          <div className="text-sm text-slate-400">
            {filteredCases.length} of {cases.length} cases
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            <Input
              placeholder="Search by client name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-48 bg-slate-800 border-slate-700 text-white">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="documents_required">Documents Required</SelectItem>
              <SelectItem value="under_review">Under Review</SelectItem>
              <SelectItem value="proposal_drafting">Proposal Drafting</SelectItem>
              <SelectItem value="creditor_voting">Creditor Voting</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Cases Grid */}
        <div className="grid gap-4">
          {filteredCases.map((c) => (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-slate-800 rounded-2xl p-6 border border-slate-700 hover:border-slate-600 transition-colors"
            >
              <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                {/* Client Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-3">
                    <div className="w-12 h-12 bg-[#10B981] rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-lg">
                        {c.user?.full_name?.charAt(0) || "?"}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">{c.user?.full_name || "Unknown"}</h3>
                      <p className="text-slate-400 text-sm">{c.user?.email}</p>
                    </div>
                  </div>
                </div>

                {/* Case Details */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 lg:gap-8">
                  <div>
                    <p className="text-slate-500 text-xs uppercase mb-1">Debt</p>
                    <p className="text-white font-bold">{formatCurrency(c.debt_amount)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs uppercase mb-1">Monthly</p>
                    <p className="text-emerald-400 font-bold">{formatCurrency(c.estimated_monthly_payment)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs uppercase mb-1">Docs</p>
                    <p className="text-white">
                      {c.pending_documents > 0 ? (
                        <span className="text-amber-400">{c.pending_documents} pending</span>
                      ) : (
                        <span>{c.document_count} uploaded</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs uppercase mb-1">Created</p>
                    <p className="text-slate-300 text-sm">{formatDate(c.created_at)}</p>
                  </div>
                </div>

                {/* Status & Actions */}
                <div className="flex items-center gap-4">
                  <Select 
                    value={c.status} 
                    onValueChange={(value) => updateCaseStatus(c.id, value)}
                  >
                    <SelectTrigger className={`w-44 ${STATUS_COLORS[c.status] || 'bg-slate-500/20 text-slate-400'}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="documents_required">Documents Required</SelectItem>
                      <SelectItem value="under_review">Under Review</SelectItem>
                      <SelectItem value="proposal_drafting">Proposal Drafting</SelectItem>
                      <SelectItem value="creditor_voting">Creditor Voting</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>

                  {c.unread_messages > 0 && (
                    <span className="flex items-center gap-1 px-2 py-1 bg-blue-500/20 text-blue-400 rounded-full text-xs">
                      <MessageSquare className="w-3 h-3" />
                      {c.unread_messages}
                    </span>
                  )}

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/admin/cases/${c.id}`)}
                    className="border-slate-600 text-slate-300 hover:bg-slate-700"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    View
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}

          {filteredCases.length === 0 && (
            <div className="bg-slate-800 rounded-2xl p-12 border border-slate-700 text-center">
              <p className="text-slate-400">No cases found</p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminCasesPage;
